import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { differenceInDays, isPast, parseISO } from "date-fns";
import { useExpiryItems, useArchivedItems } from "../hooks/useExpiryItems";
import { APP_NAME } from "../constants/expiry";
import api from "../config/api";

// ─── Types ────────────────────────────────────────────────────────────────────
type Tab = "help" | "feedback";

interface Message {
  id: number;
  from: "user" | "bot";
  text: string;
}

// ─── FAQ knowledge base (local fallback) ──────────────────────────────────────
const FAQ: { keywords: string[]; answer: string }[] = [
  {
    keywords: ["remind", "reminder", "notification", "alert", "email"],
    answer:
      'Reminders are sent automatically at 30 days and 7 days before a document expires. The "Next Reminder Mail" column in the table shows exactly when the next reminder goes out.',
  },
  {
    keywords: ["archive", "archiv"],
    answer:
      'Click the ⋮ menu on any row and choose "Archive". Archived documents move to the Archive page and can be restored at any time.',
  },
  {
    keywords: ["renew", "renewal", "renewing"],
    answer:
      'Click "Renew" in the Action column or open a document and use "Mark as Renewal" to set a new expiry date. This resets the reminder cycle and logs a Renewed event in History.',
  },
  {
    keywords: ["edit", "expiry", "date", "update", "change date"],
    answer:
      'Use the pencil ✏️ icon next to "Expiry Date" inside the Edit Document popup to directly correct the date without triggering a renewal.',
  },
  {
    keywords: ["bulk", "multiple", "select", "selection"],
    answer:
      "Check the checkboxes on the left of rows to select multiple documents. A bulk action bar appears at the bottom with Renew, Edit Expiry Date, Assign Owner, Category, Snooze, Archive and Export.",
  },
  {
    keywords: [
      "add",
      "create",
      "new",
      "import",
      "upload",
      "scan",
      "ai",
      "photo",
    ],
    answer:
      'Click "✨ Scan & Add" in the top header to upload a PDF or image — AI will automatically extract the document name, category, expiry date, and notes. You can also fill the form manually, or bulk-import via CSV.',
  },
  {
    keywords: ["history", "log", "audit", "trail", "changes"],
    answer:
      "Every document has a History tab inside its edit popup. Actions like edits, renewals, archiving and restoring are all logged there automatically.",
  },
  {
    keywords: ["export", "csv", "download"],
    answer:
      'Select one or more documents with checkboxes, then click "Export CSV" in the bulk action bar.',
  },
  {
    keywords: ["owner", "assign", "who"],
    answer:
      'Open a document to assign owners (registered users or any email address), or select multiple documents and use "Assign Owner" in the bulk action bar.',
  },
];

const FALLBACK =
  "I'm not sure about that one. Try rephrasing your question, or reach us at hello@sentrilog.com for direct support.";

const FAQ_CHIPS = [
  "How do reminders work?",
  "How do I scan a document with AI?",
  "How do I renew a document?",
  "How do I archive a document?",
  "How to bulk edit?",
  "How do I assign owners?",
  "How do I export to CSV?",
];

// ─── Small components ─────────────────────────────────────────────────────────
const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg
    className={`h-7 w-7 transition-colors ${filled ? "text-[#F59E0B]" : "text-[#D1D5DB]"}`}
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

const RATING_LABELS = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;

// ─── Main component ───────────────────────────────────────────────────────────
const ChatWidget: React.FC = () => {
  // Live data for status counts (badge on button)
  const { data: items = [] } = useExpiryItems();
  const { data: archived = [] } = useArchivedItems();

  const counts = useMemo(() => {
    const now = new Date();
    const expired = items.filter(
      (i) => i.expiry_date && isPast(parseISO(i.expiry_date))
    ).length;
    const highRisk = items.filter((i) => {
      if (!i.expiry_date || isPast(parseISO(i.expiry_date))) return false;
      return differenceInDays(parseISO(i.expiry_date), now) <= 7;
    }).length;
    const missing = items.filter((i) => !i.expiry_date).length;
    return {
      expired,
      highRisk,
      missing,
      critical: expired + highRisk + missing,
      archivedCount: archived.length,
      total: items.length,
    };
  }, [items, archived]);

  // ── Panel open/close ────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("help");

  // ── Drag state — panel ───────────────────────────────────────────────────────
  // null = use default CSS positioning (bottom-right corner)
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Drag state — bubble ───────────────────────────────────────────────────────
  const BUBBLE_SIZE = 56; // h-14 w-14
  const [bubblePos, setBubblePos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const isBubbleDraggingRef = useRef(false);
  const hasBubbleDraggedRef = useRef(false);
  const bubbleDragOffsetRef = useRef({ x: 0, y: 0 });
  const bubbleRef = useRef<HTMLDivElement>(null);

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    // Only drag on left-click directly on the header (not on close button)
    if (e.button !== 0) return;
    if (!panelRef.current) return;

    const rect = panelRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    // Immediately capture current absolute position
    setPanelPos({ top: rect.top, left: rect.left });
    isDraggingRef.current = true;
    e.preventDefault();
  }, []);

  const handleBubbleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!bubbleRef.current) return;
      const rect = bubbleRef.current.getBoundingClientRect();
      bubbleDragOffsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      setBubblePos({ top: rect.top, left: rect.left });
      isBubbleDraggingRef.current = true;
      hasBubbleDraggedRef.current = false;
      e.preventDefault();
    },
    []
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      // Panel drag
      if (isDraggingRef.current) {
        const newLeft = e.clientX - dragOffsetRef.current.x;
        const newTop = e.clientY - dragOffsetRef.current.y;
        setPanelPos({
          left: Math.max(0, Math.min(window.innerWidth - PANEL_WIDTH, newLeft)),
          top: Math.max(
            0,
            Math.min(window.innerHeight - PANEL_HEIGHT, newTop)
          ),
        });
      }
      // Bubble drag
      if (isBubbleDraggingRef.current) {
        hasBubbleDraggedRef.current = true;
        const newLeft = e.clientX - bubbleDragOffsetRef.current.x;
        const newTop = e.clientY - bubbleDragOffsetRef.current.y;
        setBubblePos({
          left: Math.max(
            0,
            Math.min(window.innerWidth - BUBBLE_SIZE, newLeft)
          ),
          top: Math.max(
            0,
            Math.min(window.innerHeight - BUBBLE_SIZE, newTop)
          ),
        });
      }
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      if (isBubbleDraggingRef.current) {
        isBubbleDraggingRef.current = false;
        // If barely moved → treat as a click (toggle panel)
        if (!hasBubbleDraggedRef.current) {
          setIsOpen((v) => {
            if (!v && bubblePos) {
              // Position panel above/left of the bubble when opening
              setPanelPos({
                left: Math.max(
                  0,
                  Math.min(
                    window.innerWidth - PANEL_WIDTH,
                    bubblePos.left - PANEL_WIDTH + BUBBLE_SIZE
                  )
                ),
                top: Math.max(0, bubblePos.top - PANEL_HEIGHT - 8),
              });
            }
            return !v;
          });
        }
        hasBubbleDraggedRef.current = false;
      }
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubblePos]);

  // Reset drag position when panel is closed
  const handleClose = () => {
    setIsOpen(false);
    setPanelPos(null);
  };

  // ── Help / AI chat state ─────────────────────────────────────────────────────
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 0,
      from: "bot",
      text: `Hi there! 👋 I'm the ${APP_NAME} AI assistant. Ask me anything about the app, or pick a topic below.`,
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [supportMode, setSupportMode] = useState<
    "idle" | "open" | "sending" | "sent"
  >("idle");
  const [supportText, setSupportText] = useState("");
  const [supportError, setSupportError] = useState<string | null>(null);

  // ── Feedback state ───────────────────────────────────────────────────────────
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [hoverRating, setHoverRating] = useState(0);

  const msgIdRef = useRef(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isAiTyping]);

  // Focus input when opening help tab
  useEffect(() => {
    if (tab === "help" && isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [tab, isOpen]);

  const addMessage = (from: "user" | "bot", text: string) => {
    setMessages((prev) => [...prev, { id: msgIdRef.current++, from, text }]);
  };

  // ── AI-powered send ──────────────────────────────────────────────────────────
  const askAI = useCallback(
    async (userMessage: string) => {
      setIsAiTyping(true);
      try {
        const history = messages.slice(-10).map((m) => ({
          role: m.from === "user" ? "user" : ("assistant" as const),
          content: m.text,
        }));
        const res = await api.post("/chat", { message: userMessage, history });
        addMessage("bot", res.data.reply);
      } catch {
        // Fallback to local FAQ
        const lower = userMessage.toLowerCase();
        const match = FAQ.find((f) =>
          f.keywords.some((kw) => lower.includes(kw))
        );
        addMessage("bot", match?.answer ?? FALLBACK);
      } finally {
        setIsAiTyping(false);
      }
    },
    [messages] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isAiTyping) return;
    addMessage("user", trimmed);
    setInputValue("");
    askAI(trimmed);
  };

  const handleFAQChip = (question: string) => {
    addMessage("user", question);
    setShowTopics(false);
    askAI(question);
  };

  // ── Contact-support email ─────────────────────────────────────────────────────
  const handleSendSupport = async () => {
    const trimmed = supportText.trim();
    if (!trimmed) return;
    setSupportMode("sending");
    setSupportError(null);
    try {
      // Attach the last 6 turns as context so support gets the full picture
      const history = messages
        .slice(-6)
        .map((m) => `${m.from === "user" ? "User" : "AI"}: ${m.text}`)
        .join("\n");
      await api.post("/feedback", {
        rating: 0,
        feedbackText: `[Support Request]\n\n${trimmed}\n\n---\nChat context:\n${history}`,
      });
      setSupportMode("sent");
      setTimeout(() => {
        setSupportMode("idle");
        setSupportText("");
        setSupportError(null);
      }, 3500);
    } catch {
      setSupportError(
        "Couldn't send — email us directly at hello@sentrilog.com"
      );
      setSupportMode("open");
    }
  };

  // ── Feedback submit ──────────────────────────────────────────────────────────
  const handleFeedbackSubmit = async () => {
    if (rating === 0 && !feedbackText.trim()) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await api.post("/feedback", { rating, feedbackText });
      setFeedbackDone(true);
    } catch (err: any) {
      setFeedbackError(
        err?.response?.data?.error ||
          "Failed to send feedback. Please try again."
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // ── Panel positioning ─────────────────────────────────────────────────────────
  const panelStyle: React.CSSProperties = panelPos
    ? {
        width: PANEL_WIDTH,
        maxWidth: "calc(100vw - 2rem)",
        height: PANEL_HEIGHT,
        top: panelPos.top,
        left: panelPos.left,
      }
    : {
        width: PANEL_WIDTH,
        maxWidth: "calc(100vw - 2rem)",
        height: PANEL_HEIGHT,
      };

  const panelPositionClass = panelPos
    ? "fixed z-[60] flex flex-col bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.2)] border border-[#E5E7EB] overflow-hidden"
    : "fixed bottom-[4.5rem] right-4 sm:right-6 z-[60] flex flex-col bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.2)] border border-[#E5E7EB] overflow-hidden";

  return (
    <>
      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      {isOpen && (
        <div ref={panelRef} className={panelPositionClass} style={panelStyle}>
          {/* ── Header (draggable) ────────────────────────────────────────── */}
          <div
            className="flex-shrink-0 bg-[#2563EB] px-4 py-3 flex items-center justify-between select-none"
            style={{ cursor: isDraggingRef.current ? "grabbing" : "grab" }}
            onMouseDown={handleHeaderMouseDown}
          >
            <div className="flex items-center gap-2.5 pointer-events-none">
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                {/* Sparkle/AI icon */}
                <svg
                  className="h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white leading-tight">
                  {APP_NAME} AI
                </p>
                <p className="text-[10px] text-blue-200 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 inline-block" />
                  Ask me anything · drag to move
                </p>
              </div>
            </div>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleClose}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/20 transition-colors pointer-events-auto"
              aria-label="Close"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* ── Tabs (Help + Feedback only) ───────────────────────────────── */}
          <div className="flex-shrink-0 flex border-b border-[#E5E7EB] bg-white">
            {(
              [
                { id: "help", label: "Ask AI", icon: "✨" },
                { id: "feedback", label: "Feedback", icon: "⭐" },
              ] as { id: Tab; label: string; icon: string }[]
            ).map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                  tab === id
                    ? "border-[#2563EB] text-[#2563EB]"
                    : "border-transparent text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                <span>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          {/* ── HELP / AI TAB ─────────────────────────────────────────────── */}
          {tab === "help" && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.from === "bot" && (
                      <div className="h-6 w-6 rounded-full bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 mr-1.5 mt-0.5">
                        <svg
                          className="h-3.5 w-3.5 text-[#2563EB]"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                          />
                        </svg>
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                        msg.from === "user"
                          ? "bg-[#2563EB] text-white rounded-tr-sm"
                          : "bg-[#F3F4F6] text-[#374151] rounded-tl-sm"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}

                {/* AI typing indicator */}
                {isAiTyping && (
                  <div className="flex justify-start">
                    <div className="h-6 w-6 rounded-full bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 mr-1.5 mt-0.5">
                      <svg
                        className="h-3.5 w-3.5 text-[#2563EB]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                        />
                      </svg>
                    </div>
                    <div className="bg-[#F3F4F6] px-3 py-2.5 rounded-2xl rounded-tl-sm flex items-center gap-1">
                      <span
                        className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <span
                        className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      />
                      <span
                        className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                )}

                {/* FAQ chips — on first open OR when "Browse topics" chip is clicked */}
                {(messages.length === 1 || showTopics) && !isAiTyping && (
                  <div className="mt-1 space-y-1.5">
                    <div className="flex items-center justify-between pl-8 pr-1">
                      <p className="text-[10px] text-[#9CA3AF] font-medium uppercase tracking-wide">
                        Suggested topics
                      </p>
                      {/* Only show close ✕ when manually toggled (not on first open) */}
                      {showTopics && (
                        <button
                          onClick={() => setShowTopics(false)}
                          className="p-0.5 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                          aria-label="Close topics"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="pl-8 flex flex-wrap gap-1.5">
                      {FAQ_CHIPS.map((q) => (
                        <button
                          key={q}
                          onClick={() => handleFAQChip(q)}
                          className="px-2.5 py-1.5 rounded-full border border-[#E5E7EB] text-[11px] text-[#374151] hover:bg-[#EFF6FF] hover:border-[#BFDBFE] hover:text-[#2563EB] transition-colors leading-tight"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* ── Input / Support form ──────────────────────────────── */}
              <div className="flex-shrink-0 border-t border-[#E5E7EB] p-3">
                {/* ── Support: success state ──────────────────────────── */}
                {supportMode === "sent" ? (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs font-medium text-[#15803D] bg-[#DCFCE7] rounded-xl">
                    <svg
                      className="h-4 w-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Sent! We'll reply to your inbox shortly.
                  </div>
                ) : supportMode !== "idle" ? (
                  /* ── Support: compose form ─────────────────────────── */
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-[#111827] flex items-center gap-1.5">
                        <svg
                          className="h-3.5 w-3.5 text-[#2563EB]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Contact Support
                      </p>
                      <button
                        onClick={() => {
                          setSupportMode("idle");
                          setSupportText("");
                          setSupportError(null);
                        }}
                        className="p-0.5 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                        aria-label="Cancel"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <textarea
                      value={supportText}
                      onChange={(e) => setSupportText(e.target.value)}
                      rows={3}
                      placeholder="Describe your issue and we'll get back to you…"
                      className="w-full text-xs border border-[#E5E7EB] rounded-lg px-3 py-2 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] resize-none leading-relaxed"
                    />
                    {supportError && (
                      <p className="text-[10px] text-[#DC2626] bg-[#FEF2F2] rounded px-2 py-1">
                        {supportError}
                      </p>
                    )}
                    <button
                      onClick={handleSendSupport}
                      disabled={
                        !supportText.trim() || supportMode === "sending"
                      }
                      className="w-full py-2 bg-[#2563EB] text-white text-xs font-semibold rounded-lg hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
                    >
                      {supportMode === "sending" ? (
                        <>
                          <svg
                            className="h-3.5 w-3.5 animate-spin"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        "Send to Support"
                      )}
                    </button>
                    <p className="text-[9px] text-[#9CA3AF] text-center">
                      Sent to hello@sentrilog.com · We reply within 24 h
                    </p>
                  </div>
                ) : (
                  /* ── Normal chat input ─────────────────────────────── */
                  <>
                    <div className="flex gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        placeholder="Ask anything about the app…"
                        disabled={isAiTyping}
                        className="flex-1 text-xs border border-[#E5E7EB] rounded-lg px-3 py-2 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] disabled:opacity-50"
                      />
                      <button
                        onClick={handleSend}
                        disabled={!inputValue.trim() || isAiTyping}
                        className="px-3 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        {isAiTyping ? (
                          <svg
                            className="h-3.5 w-3.5 animate-spin"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                            />
                          </svg>
                        )}
                      </button>
                    </div>

                    {/* ── Toolbar chips ───────────────────────────────── */}
                    <div className="flex items-center justify-between mt-2">
                      {/* Browse topics chip */}
                      <button
                        onClick={() => setShowTopics((v) => !v)}
                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full border transition-colors ${
                          showTopics
                            ? "bg-[#EFF6FF] border-[#BFDBFE] text-[#2563EB]"
                            : "border-[#E5E7EB] text-[#6B7280] hover:border-[#D1D5DB] hover:text-[#374151]"
                        }`}
                      >
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 10h16M4 14h8"
                          />
                        </svg>
                        Browse topics
                      </button>

                      {/* Contact support chip */}
                      <button
                        onClick={() => {
                          const lastUserMsg = [...messages]
                            .reverse()
                            .find((m) => m.from === "user");
                          setSupportText(lastUserMsg?.text ?? "");
                          setSupportMode("open");
                        }}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full border border-[#E5E7EB] text-[#6B7280] hover:border-[#FECACA] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                      >
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Contact support
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* ── FEEDBACK TAB ──────────────────────────────────────────────── */}
          {tab === "feedback" && (
            <div className="flex-1 overflow-y-auto p-4">
              {feedbackDone ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3">
                  <div className="h-14 w-14 rounded-full bg-[#DCFCE7] flex items-center justify-center">
                    <svg
                      className="h-7 w-7 text-[#15803D]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-[#111827]">
                    Thank you! 🎉
                  </p>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Your feedback helps us improve {APP_NAME}. We read every
                    submission.
                  </p>
                  <button
                    onClick={() => {
                      setFeedbackDone(false);
                      setRating(0);
                      setFeedbackText("");
                      setFeedbackError(null);
                    }}
                    className="mt-2 text-xs text-[#2563EB] hover:underline"
                  >
                    Submit another response
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Star rating */}
                  <div>
                    <p className="text-xs font-medium text-[#374151] mb-2.5">
                      How would you rate {APP_NAME}?
                    </p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="transition-transform hover:scale-110 focus:outline-none"
                          aria-label={`Rate ${star}`}
                        >
                          <StarIcon filled={star <= (hoverRating || rating)} />
                        </button>
                      ))}
                      {(hoverRating || rating) > 0 && (
                        <span className="ml-2 text-xs font-medium text-[#374151]">
                          {RATING_LABELS[hoverRating || rating]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Feedback textarea */}
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">
                      Tell us more — suggestions, bugs, feature requests
                    </label>
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      rows={5}
                      placeholder="e.g. It would be great if reminders could be customised per document…"
                      className="w-full text-xs border border-[#E5E7EB] rounded-lg px-3 py-2 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] resize-none leading-relaxed"
                    />
                  </div>

                  {feedbackError && (
                    <p className="text-xs text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">
                      {feedbackError}
                    </p>
                  )}

                  <button
                    onClick={handleFeedbackSubmit}
                    disabled={
                      feedbackSubmitting ||
                      (rating === 0 && !feedbackText.trim())
                    }
                    className="w-full py-2.5 bg-[#2563EB] text-white text-sm font-medium rounded-lg hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {feedbackSubmitting ? (
                      <>
                        <svg
                          className="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      "Submit Feedback"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Floating button + tooltip ─────────────────────────────────────── */}
      <div
        ref={bubbleRef}
        className="fixed z-[60] flex items-center group"
        style={
          bubblePos
            ? { top: bubblePos.top, left: bubblePos.left }
            : { bottom: "1rem", right: "1.5rem" }
        }
        onMouseDown={handleBubbleMouseDown}
      >
        {/* Tooltip — only visible on hover, never intercepts pointer events */}
        {!isOpen && (
          <div className="absolute right-full mr-3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="relative bg-[#1E293B] text-white text-[11px] font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
              ✨ Ask AI anything
              {/* Arrow pointing right */}
              <span className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[5px] border-l-[#1E293B]" />
            </div>
          </div>
        )}
        <button
          style={{ cursor: isBubbleDraggingRef.current ? "grabbing" : "grab" }}
          className={`relative h-14 w-14 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.2)] flex items-center justify-center transition-all ${
            isOpen
              ? "bg-[#374151] scale-95"
              : "bg-[#2563EB] hover:bg-[#1D4ED8] hover:scale-105"
          }`}
          aria-label={isOpen ? "Close AI assistant" : "Open AI assistant"}
        >
          {isOpen ? (
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <>
              {/* Sparkle icon */}
              <svg
                className="h-6 w-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
              {counts.critical > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-[#DC2626] border-2 border-white text-white text-[9px] font-bold flex items-center justify-center px-1 pointer-events-none">
                  {counts.critical > 9 ? "9+" : counts.critical}
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default ChatWidget;
